import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Input, InputNumber, Row, Form, DatePicker } from 'antd';
import dayjs from '../../utils/dayjs';
import RadioGroup from '../../atoms/RadioGroup';
import { VDSRequestResultModal } from '../../containers';
import {
  STATISTICS_TYPE_GHG,
  STATISTICS_TYPE_VDS,
} from '../../constants/StatisticsTypes';
import { getStatisticsTypeOptions } from '../../utils/vdsHelpers';
import { v4 as uuidv4 } from 'uuid';

const typeOptions = getStatisticsTypeOptions();

export class VDSTransmissionStatisticsForm extends Component {
  static propTypes = {
    onSubmit: PropTypes.func,
    onReset: PropTypes.func,
    children: PropTypes.any,
    request: PropTypes.any,
    response: PropTypes.any,
  };

  constructor(props) {
    super(props);
    this.formRef = React.createRef();
    this.state = {
      validated: false,
      type: STATISTICS_TYPE_VDS,
      resultsModalShown: false,
    };
  }

  componentDidMount() {
    this.resetTransactionDateTime();
    this.resetTransferID();
  }

  resetTransferID() {
    this.formRef.current?.setFieldsValue({ TransferID: uuidv4() });
  }

  isFirstMarketSale() {
    const values = this.formRef.current?.getFieldsValue() || {};

    return (
      !!values.TransferSource?.SourceID ||
      !!values.Purchased?.QuantityEnergyNatural
    );
  }

  isSecondMarketSale() {
    const values = this.formRef.current?.getFieldsValue() || {};

    return (
      !!values.TransferSource?.OfferID ||
      !!values.Purchased?.GHGAmount ||
      !!values.Purchased?.QuantityEnergyNatural2
    );
  }

  resetTransactionDateTime() {
    this.formRef.current?.setFieldsValue({ TransactionDateTime: dayjs() });
  }

  submit = () => {
    this.setState({ validated: true });
    this.formRef.current?.validateFields().then((values) => {
      this.props.onSubmit?.(values);
    });
  };

  reset = () => {
    this.formRef.current?.resetFields();
    this.resetTransactionDateTime();
    this.resetTransferID();
    this.setState({ validated: false });
    this.props.onReset?.();
  };

  onTypeChange = (event) => {
    this.setState({ type: event.target.value });
  };

  showResult = () => {
    this.setState({ resultsModalShown: true });
  };

  hideResult = () => {
    this.setState({ resultsModalShown: false });
  };

  render() {
    const { children, request, response } = this.props;
    const { type, resultsModalShown } = this.state;
    const firstMarketSale = this.isFirstMarketSale();
    const secondMarketSale = this.isSecondMarketSale();

    return (
      <>
        <Form
          ref={this.formRef}
          onFinish={this.submit}
          layout="vertical"
          onValuesChange={() => this.forceUpdate()}
        >
          <h1>Elektrienergia või vedelkütuse statistika ülekanne</h1>
          <h2>Päise parameetrid</h2>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="TransferID"
                name="TransferID"
                rules={[
                  { required: true, message: 'Sisesta TransferID' },
                  { max: 50, message: 'Maksimaalne pikkus on 50' },
                ]}
              >
                <Input placeholder="TransferID" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="TransactionDateTime"
                name="TransactionDateTime"
                rules={[
                  { required: true, message: 'Sisesta TransactionDateTime' },
                ]}
              >
                <DatePicker
                  className="w-100"
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder="TransactionDateTime"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="BuyerCode"
                name="BuyerCode"
                rules={[
                  { required: true, message: 'Sisesta BuyerCode' },
                  { max: 8, message: 'Maksimaalne pikkus on 8' },
                ]}
              >
                <Input placeholder="BuyerCode" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="DocumentNo"
                name="DocumentNo"
                rules={[
                  { required: true, message: 'Sisesta DocumentNo' },
                  { max: 50, message: 'Maksimaalne pikkus on 50' },
                ]}
              >
                <Input placeholder="DocumentNo" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <h2>{'Esimese turu müük'}</h2>
              <div className={'block'}>
                {!secondMarketSale ? (
                  <Form.Item
                    key="SourceID"
                    label="TransferSource"
                    name={['TransferSource', 'SourceID']}
                    rules={[
                      {
                        required: true,
                        message: 'Sisesta TransferSource SourceID',
                      },
                      { max: 50, message: 'Maksimaalne pikkus on 50' },
                    ]}
                  >
                    <Input placeholder="SourceID" />
                  </Form.Item>
                ) : (
                  <Form.Item key="SourceID_" label="TransferSource">
                    <Input placeholder="SourceID" disabled />
                  </Form.Item>
                )}
                {!secondMarketSale ? (
                  <Form.Item
                    key="QuantityEnergyNatural"
                    label="Purchased"
                    name={['Purchased', 'QuantityEnergyNatural']}
                    rules={[
                      {
                        required: true,
                        message: 'Sisesta Purchased QuantityEnergyNatural',
                      },
                    ]}
                  >
                    <InputNumber
                      className="w-100"
                      min={0.1}
                      max={99999999999999.9}
                      step={0.1}
                      precision={1}
                      placeholder="QuantityEnergyNatural (MJ)"
                    />
                  </Form.Item>
                ) : (
                  <Form.Item key="QuantityEnergyNatural_" label="Purchased">
                    <InputNumber
                      className="w-100"
                      placeholder="QuantityEnergyNatural (MJ)"
                      disabled
                    />
                  </Form.Item>
                )}
              </div>
            </Col>
            <Col span={12}>
              <h2>{'Teise turu müük'}</h2>
              <div className={'block'}>
                {!firstMarketSale ? (
                  <Form.Item
                    key="OfferID"
                    label="TransferSource"
                    name={['TransferSource', 'OfferID']}
                    rules={[
                      {
                        required: true,
                        message: 'Sisesta TransferSource OfferID',
                      },
                      { max: 50, message: 'Maksimaalne pikkus on 50' },
                    ]}
                  >
                    <Input placeholder="OfferID" />
                  </Form.Item>
                ) : (
                  <Form.Item key="OfferID_" label="TransferSource">
                    <Input placeholder="OfferID" disabled />
                  </Form.Item>
                )}
                <Form.Item label="Source type">
                  <RadioGroup
                    options={typeOptions}
                    valueKey="value"
                    labelKey="name"
                    value={type}
                    disabled={firstMarketSale}
                    onChange={this.onTypeChange}
                  />
                </Form.Item>
                {firstMarketSale && (
                  <Form.Item key="Purchased_" label="Purchased">
                    <InputNumber
                      className="w-100"
                      disabled
                      placeholder={
                        type === STATISTICS_TYPE_GHG
                          ? 'GHGAmount (t)'
                          : 'QuantityEnergyNatural (MJ)'
                      }
                    />
                  </Form.Item>
                )}
                {!firstMarketSale && type === STATISTICS_TYPE_GHG && (
                  <Form.Item
                    key="Purchased"
                    label="Purchased"
                    name={['Purchased', 'GHGAmount']}
                    rules={[
                      {
                        required: !firstMarketSale,
                        message: 'Sisesta Purchased GHGAmount',
                      },
                    ]}
                  >
                    <InputNumber
                      className="w-100"
                      min={-99999999999999.9}
                      max={-0.1}
                      step={0.1}
                      precision={1}
                      disabled={firstMarketSale}
                      placeholder="GHGAmount (t)"
                    />
                  </Form.Item>
                )}
                {!firstMarketSale && type === STATISTICS_TYPE_VDS && (
                  <Form.Item
                    key="Purchased"
                    label="Purchased"
                    name={['Purchased', 'QuantityEnergyNatural2']}
                    rules={[
                      {
                        required: !firstMarketSale,
                        message: 'Sisesta Purchased QuantityEnergyNatural',
                      },
                    ]}
                  >
                    <InputNumber
                      className="w-100"
                      min={0.1}
                      max={99999999999999.9}
                      step={0.1}
                      precision={1}
                      disabled={firstMarketSale}
                      placeholder="QuantityEnergyNatural (MJ)"
                    />
                  </Form.Item>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button htmlType="submit" type="primary">
                Saada
              </Button>
              <Button onClick={this.reset}>Puhasta</Button>
              <Button onClick={this.showResult} disabled={!request}>
                Päringu tulemus
              </Button>
            </Col>
          </Row>
          {children}
          <VDSRequestResultModal
            request={request}
            response={response}
            show={resultsModalShown}
            hideModal={this.hideResult}
          />
        </Form>
      </>
    );
  }
}

export default VDSTransmissionStatisticsForm;
